/**
 * TaxRateBanner — tells operators a venue needs a sales-tax rate set by hand
 * (BON-387 / design.md D7).
 *
 * Read-only and undismissable, like its sibling DriftBanner. The rate is set from
 * the Sales tax section on the Details tab, and the banner clears once it is.
 *
 * Note for anyone aligning the two banners later: the leading icon here is per the
 * operator's design for THIS banner. DriftBanner having none is not an oversight —
 * do not "fix" it to match without checking.
 *
 * Fires on a null rate ONLY. A rate of 0 is a real rate — a confirmed no-tax
 * jurisdiction, which is every NH and OR venue — so it stays quiet even though 0
 * and null both charge nothing. Treating 0 as a gap would nag operators forever
 * about venues that are already correct.
 *
 * D7 as recorded scopes this alert to Nebari-managed venues, on the grounds that
 * an unmanaged venue's rate is never read at checkout. Deliberately broader here
 * (operator's call, 2026-08-17): once the FareHarbor sync has run, the only
 * unrated venues left are the ones that genuinely need a hand-set rate, and
 * surfacing them before cutover is more useful than discovering them at it.
 *
 * `role="status"` (polite), deliberately not `role="alert"` (assertive). A live
 * region only announces a MUTATION to a region already in the accessibility tree,
 * so an `alert` present on first paint typically announces nothing — while the same
 * component mounted during an in-app navigation between venues does announce,
 * making the behaviour depend on how the reader arrived. `alert` is also the wrong
 * register: it means "stop what you are doing", which fits a validation error after
 * Save, not a standing fact about a venue. And two assertive regions — this plus
 * DriftBanner, which both mount for an unrated venue that also has drift — can
 * interrupt one another. DriftBanner was switched to match.
 */
import { AlertCircle } from 'lucide-react';

export function TaxRateBanner({ taxPercent }: { taxPercent: number | null }) {
  if (taxPercent !== null) return null;

  return (
    <section
      className="nb-card"
      role="status"
      style={{
        flex: '0 0 auto',
        borderLeft: '3px solid var(--nb-warn)',
        padding: '12px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <AlertCircle size={16} color="var(--nb-warn)" aria-hidden="true" />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--nb-ink)' }}>
          No tax rate assigned
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--nb-muted)' }}>
          Bookings at this venue will not be taxed until a rate is set.
        </span>
      </div>
    </section>
  );
}
